/**
 * Caption state at a playback time.
 *
 * Given segments + words + time `tMs`, compute exactly which segment is on
 * screen, which word is active, and entrance/exit progress. Both the browser
 * render loop and the frame-by-frame export call this so "which word is lit at
 * time T" is answered identically.
 *
 * Segments are assumed sorted by startMs and non-overlapping (the invariant the
 * segmentation engine and editor maintain).
 */
export const DEFAULT_STATE_OPTIONS = {
    entranceMs: 180,
    exitMs: 120,
};
function clamp01(x) {
    return x < 0 ? 0 : x > 1 ? 1 : x;
}
/**
 * Binary-search the active segment for `tMs`, expanding each segment's window
 * by the entrance/exit lead/lag so animations have room to play.
 */
export function findActiveSegment(segments, tMs, opts = DEFAULT_STATE_OPTIONS) {
    let lo = 0;
    let hi = segments.length - 1;
    let best = null;
    while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        const seg = segments[mid];
        const start = seg.startMs - opts.entranceMs;
        const end = seg.endMs + opts.exitMs;
        if (tMs < start) {
            hi = mid - 1;
        }
        else if (tMs > end) {
            lo = mid + 1;
        }
        else {
            best = seg;
            break;
        }
    }
    return best;
}
export function captionStateAt(segments, wordsById, tMs, opts = DEFAULT_STATE_OPTIONS) {
    const segment = findActiveSegment(segments, tMs, opts);
    if (!segment) {
        return {
            segment: null,
            words: [],
            nowMs: tMs,
            activeWordIndex: -1,
            entranceProgress: 0,
            exitProgress: 0,
        };
    }
    const words = [];
    let activeWordIndex = -1;
    segment.wordIds.forEach((id, index) => {
        const word = wordsById.get(id);
        if (!word)
            return;
        let phase;
        let activeProgress;
        if (tMs < word.startMs) {
            phase = 'future';
            activeProgress = 0;
        }
        else if (tMs >= word.endMs) {
            phase = 'past';
            activeProgress = 1;
        }
        else {
            phase = 'active';
            const dur = Math.max(1, word.endMs - word.startMs);
            activeProgress = clamp01((tMs - word.startMs) / dur);
            activeWordIndex = words.length;
        }
        words.push({ word, index, phase, activeProgress });
    });
    // If we're between two words (in a micro-gap) the active index is -1; treat
    // the most recent past word as "current" for active-word templates so the
    // highlight doesn't flicker off during short gaps.
    if (activeWordIndex === -1) {
        for (let i = words.length - 1; i >= 0; i--) {
            if (words[i].phase === 'past') {
                // Only hold if the next word hasn't started and we're still inside seg.
                const next = words[i + 1];
                if (!next || tMs < next.word.startMs) {
                    activeWordIndex = i;
                }
                break;
            }
        }
    }
    const entranceProgress = clamp01((tMs - (segment.startMs - opts.entranceMs)) / opts.entranceMs);
    const exitProgress = tMs <= segment.endMs ? 0 : clamp01((tMs - segment.endMs) / opts.exitMs);
    return { segment, words, nowMs: tMs, activeWordIndex, entranceProgress, exitProgress };
}
/** Convenience: the currently-spoken TranscriptWord across the whole transcript. */
export function activeWordAt(words, tMs) {
    // Linear scan is fine for the transcript panel highlight; the hot render path
    // uses captionStateAt with per-segment scope.
    for (const w of words) {
        if (tMs >= w.startMs && tMs < w.endMs)
            return w;
    }
    return null;
}
//# sourceMappingURL=state.js.map